import React, { useState, useRef, useEffect, MouseEvent } from 'react';
import classNames from 'classnames';
import styles from './videoCard.scss';
import { VideoSource } from '../../types';
import TabNavigationHelper from '../../helper/tabNavigationHelper';

export interface VideoCardProps {
    srcSet: VideoSource[];
    poster?: string;
    ariaLabel?: string;
    autoPlay?: boolean;
    loop?: boolean;
    debug?: boolean;
    has3DView?: boolean;
    className?: string;
}

const VideoCard: React.FC<VideoCardProps> = ({
    srcSet,
    poster,
    ariaLabel,
    autoPlay = false,
    loop = false,
    debug = false,
    has3DView = false,
    className,
}) => {
    const tabNavigation = TabNavigationHelper.instance;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isControlsVisible, setIsControlsVisible] = useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<string>('Paused');

    const showPlayingControlTemporarily = (): void => {
        setIsControlsVisible(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

        hideTimerRef.current = setTimeout(() => {
            if (
                videoRef.current &&
                !videoRef.current.paused &&
                !videoRef.current.ended
            ) {
                setIsControlsVisible(false);
            }
        }, 1400);
    };

    const togglePlayback = async (
        event?: MouseEvent<HTMLElement>,
    ): Promise<void> => {
        event?.stopPropagation();

        const video = videoRef.current;
        if (!video || isError) return;

        try {
            if (video.paused || video.ended) {
                await video.play();
                play3DView(false);
            } else {
                video.pause();
                play3DView(true);
            }
        } catch {
            setStatusMessage('Tap again to start the video.');
        }
    };

    const play3DView = React.useCallback(
        (play3d = false) => {
            if (has3DView) {
                window.shell.publishTo('SCS.PlayPauseAnimation', {
                    containerId: 'ThreeSixtyView_mixedMedia',
                    play3d,
                });
            }
        },
        [has3DView],
    );

    const clearControlsTimer = (): void => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const hideControls = (): void => {
        setIsControlsVisible(false);
        clearControlsTimer();
    };

    const handlePlay = (): void => {
        setIsPlaying(true);
        setIsError(false);
        setStatusMessage('Playing');
        showPlayingControlTemporarily();
    };

    const handlePause = (): void => {
        setIsPlaying(false);
        hideControls();
        setStatusMessage('Paused');
    };

    const handleEnded = (): void => {
        setIsPlaying(false);
        hideControls();
        setStatusMessage('Paused');
    };

    const handleVideoError = (): void => {
        setIsError(true);
        setIsPlaying(false);
        setIsControlsVisible(false);
        setStatusMessage('Video could not be loaded.');
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (autoPlay) {
            play3DView(false);

            void video.play().catch(() => {
                setStatusMessage('Tap to start the video.');
                play3DView(true);
            });
        }

        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
            }
        };
    }, [autoPlay]);

    return (
        <div className={classNames(styles.video_card_wrapper, className)}>
            <div
                className={classNames(styles.video_component, {
                    [styles.is_playing]: isPlaying,
                    [styles.is_controls_visible]: !isError && isControlsVisible,
                })}
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
                ref={(el) => tabNavigation.setFocusAtt(el)}
            >
                <video
                    ref={videoRef}
                    preload="metadata"
                    poster={poster}
                    autoPlay={autoPlay}
                    loop={loop}
                    playsInline
                    disablePictureInPicture
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    aria-label={ariaLabel}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    onError={handleVideoError}
                >
                    {srcSet.map((el, index) => (
                        <source
                            key={el.src + index}
                            src={el.src}
                            type={el.type ? 'video/' + el.type : ''}
                            media={
                                typeof el.maxWidthPx === 'number'
                                    ? `all and (max-width: ${el.maxWidthPx}px)`
                                    : ''
                            }
                        />
                    ))}
                    Your browser does not support HTML video.
                </video>

                {!isError && (
                    <button
                        className={classNames(styles.play_toggle, {
                            [styles.is_playing]: isPlaying,
                        })}
                        type="button"
                        aria-label={isPlaying ? 'Pause video' : 'Play video'}
                        onClick={togglePlayback}
                    >
                        <span className={styles.icon_play} aria-hidden="true" />
                        <span
                            className={styles.icon_pause}
                            aria-hidden="true"
                        />
                    </button>
                )}
            </div>

            {debug && (
                <p className={styles.status} aria-live="polite">
                    {statusMessage}
                </p>
            )}
        </div>
    );
};

export default VideoCard;
